import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { sec } from "../../../utils/security";
import { ChatModel } from "../types/model";
import { Setting } from "../types/setting";

/**
 * "Word Addin → Default Model" — the organisation's preferred chat model for
 * this add-in. Seeded by the settings service migration `AddWordAddinSection`;
 * this identifier must stay in step with that migration.
 */
export const WORD_ADDIN_DEFAULT_MODEL_SETTING = "82c6179c-8416-4566-8d6b-95b7f3e8af7b";

/**
 * "Word Addin → Available Models" — the chat models this add-in may use, as a
 * subset of the models enabled for the organisation. Seeded by the settings
 * service migration `AddWordAddinAvailableModels`.
 *
 * An empty selection means no restriction, not "nothing allowed". The service
 * also intersects the stored selection against the organisation's currently
 * enabled models on read, so a model later disabled on the Models section drops
 * out of `categories` on its own.
 */
export const WORD_ADDIN_AVAILABLE_MODELS_SETTING = "229212c9-34a4-47f5-ae36-fa831a3ac272";

export const settingsApi = createApi({
  reducerPath: "settingsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.VITE_JYLO_API_URL_SETTINGS,
    prepareHeaders: async (headers) => {
      const token = await sec.getAccessTokenSilently()();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getModels: builder.query<ChatModel[], void>({
      query: () => "models",
    }),
    // Non-admin endpoint: serves settings flagged user-visible, and 404s for
    // admin-only or unknown identifiers — including on deployments that predate
    // the Word Addin section, so callers must tolerate the error.
    getUserSetting: builder.query<Setting, string>({
      query: (objectIdentifier) => `settings/user-settings/${objectIdentifier}`,
    }),
  }),
});

export const { useGetModelsQuery, useGetUserSettingQuery } = settingsApi;
